import axios from "axios";
import { getErrorMessage, handleApiError } from "../utils/errorHandler";
import API_BASE_URL from "../config/api";

const BASE_URL = API_BASE_URL;

// Create a new liquid admixture test
export const createLiquidAdmixtureTest = async (testData) => {
  const url = `${BASE_URL}/liquid-admixture-tests`;
  try {
    console.log('🔵 [LIQUID ADMIXTURE API] Creating new test with data:', testData);
    console.log('🌐 [LIQUID ADMIXTURE API] API Base URL:', BASE_URL);
    console.log('🌐 [LIQUID ADMIXTURE API] Full URL:', url);
    const response = await axios.post(url, testData);
    console.log('✅ [LIQUID ADMIXTURE API] Test created successfully:', response.data);
    return response.data;
  } catch (error) {
    // Log error details properly to avoid "fn" issue
    const errorStatus = error.response?.status;
    const errorMessage = error.message || 'Unknown error';
    const errorData = error.response?.data;
    
    console.error("❌ [LIQUID ADMIXTURE API] Error creating test");
    console.error("   Status:", errorStatus);
    console.error("   Message:", errorMessage);
    console.error("   URL:", url);
    console.error("   Response data:", errorData);
    
    // Enhanced error message for 404
    if (errorStatus === 404) {
      const userFriendlyMessage = `Unable to save test data. The server endpoint is not available.\n\nThis usually means the backend needs to be updated. Please contact your system administrator.\n\nTechnical details: POST /api/liquid-admixture-tests returned 404`;
      const detailedMessage = `API endpoint not found (404). The backend route may not be deployed to production.\n\nExpected endpoint: POST /api/liquid-admixture-tests\nURL attempted: ${url}\n\nPlease ensure the backend is deployed with the latest code from backend/app.py (lines 3149-3247)`;
      console.error("   Details:", detailedMessage);
      handleApiError(error, userFriendlyMessage);
      const enhancedError = new Error(detailedMessage);
      enhancedError.originalError = error;
      throw enhancedError;
    }
    
    const userFriendlyMessage = getErrorMessage(error);
    handleApiError(error, "Failed to save liquid admixture test. Please try again.");
    const enhancedError = new Error(userFriendlyMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

// Get liquid admixture test by ID
export const getLiquidAdmixtureTest = async (testId) => {
  const url = `${BASE_URL}/liquid-admixture-tests/${testId}`;
  try {
    console.log('🔵 [LIQUID ADMIXTURE API] Fetching test ID:', testId);
    console.log('🌐 [LIQUID ADMIXTURE API] Full URL:', url);
    const response = await axios.get(url);
    console.log('✅ [LIQUID ADMIXTURE API] Test fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    // Log error details properly to avoid "fn" issue
    const errorStatus = error.response?.status;
    const errorMessage = error.message || 'Unknown error';
    const errorData = error.response?.data;
    
    console.error("❌ [LIQUID ADMIXTURE API] Error fetching test");
    console.error("   Status:", errorStatus);
    console.error("   Message:", errorMessage);
    console.error("   URL:", url);
    console.error("   Response data:", errorData);
    
    if (errorStatus === 404) {
      console.warn("⚠️ [LIQUID ADMIXTURE API] Endpoint not found (404). The backend route may not be deployed.");
    }
    
    throw error;
  }
};

// Update liquid admixture test
export const updateLiquidAdmixtureTest = async (testId, testData) => {
  const url = `${BASE_URL}/liquid-admixture-tests/${testId}`;
  try {
    console.log('🔵 [LIQUID ADMIXTURE API] Updating test ID:', testId, 'with data:', testData);
    console.log('🌐 [LIQUID ADMIXTURE API] Full URL:', url);
    const response = await axios.put(url, testData);
    console.log('✅ [LIQUID ADMIXTURE API] Test updated successfully:', response.data);
    return response.data;
  } catch (error) {
    // Log error details properly to avoid "fn" issue
    const errorStatus = error.response?.status;
    const errorMessage = error.message || 'Unknown error';
    const errorData = error.response?.data;
    
    console.error("❌ [LIQUID ADMIXTURE API] Error updating test");
    console.error("   Status:", errorStatus);
    console.error("   Message:", errorMessage);
    console.error("   URL:", url);
    console.error("   Response data:", errorData);
    
    // Enhanced error message for 404
    if (errorStatus === 404) {
      const userFriendlyMessage = `Unable to update test data. The server endpoint is not available.\n\nThis usually means the backend needs to be updated. Please contact your system administrator.\n\nTechnical details: PUT /api/liquid-admixture-tests/${testId} returned 404`;
      const detailedMessage = `API endpoint not found (404). The backend route may not be deployed to production.\n\nExpected endpoint: PUT /api/liquid-admixture-tests/${testId}\nURL attempted: ${url}\n\nPlease ensure the backend is deployed with the latest code from backend/app.py (lines 3314-3421)`;
      console.error("   Details:", detailedMessage);
      handleApiError(error, userFriendlyMessage);
      const enhancedError = new Error(detailedMessage);
      enhancedError.originalError = error;
      throw enhancedError;
    }
    
    const userFriendlyMessage = getErrorMessage(error);
    handleApiError(error, "Failed to update liquid admixture test. Please try again.");
    const enhancedError = new Error(userFriendlyMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

// Get all liquid admixture tests (for dashboard)
export const getAllLiquidAdmixtureTests = async () => {
  const url = `${BASE_URL}/liquid-admixture-tests`;
  try {
    console.log('🔵 [LIQUID ADMIXTURE API] Fetching all tests for dashboard');
    console.log('🌐 [LIQUID ADMIXTURE API] Full URL:', url);
    const response = await axios.get(url);
    console.log('✅ [LIQUID ADMIXTURE API] All tests fetched successfully:', response.data);
    return response.data.tests || [];
  } catch (error) {
    // Log error details properly to avoid "fn" issue
    const errorStatus = error.response?.status;
    const errorMessage = error.message || 'Unknown error';
    const errorData = error.response?.data;
    
    console.error("❌ [LIQUID ADMIXTURE API] Error fetching all tests");
    console.error("   Status:", errorStatus);
    console.error("   Message:", errorMessage);
    console.error("   URL:", url);
    console.error("   Response data:", errorData);
    
    // Warn if endpoint is not found (likely not deployed)
    if (errorStatus === 404) {
      console.warn("⚠️ [LIQUID ADMIXTURE API] Endpoint not found (404). The backend route may not be deployed to production.");
      console.warn("   Expected endpoint: POST/GET/PUT /api/liquid-admixture-tests");
      console.warn("   Please ensure the backend is deployed with the latest code from backend/app.py");
    }
    
    // Return empty array on error to not break dashboard
    return [];
  }
};
